import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useChildProfile } from "../contexts/ChildProfileContext";
import { BigButton, Card } from "../design/BigButton";
import { avatarLabel } from "../lib/assets";

const AVATAR_CHOICES = ["fox", "bear", "bunny", "cat", "frog", "panda"];

export function ProfilesManager() {
  const { profiles, refresh } = useChildProfile();
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open the "Add child" form when navigated with ?add=1 (from the
  // profile picker's empty-state / add-tile action).
  useEffect(() => {
    if (searchParams.get("add") === "1") {
      setCreating(true);
      // Strip the param so refreshes don't re-open the modal.
      const next = new URLSearchParams(searchParams);
      next.delete("add");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div>
      <h1>Child Profiles</h1>
      <BigButton onClick={() => setCreating(true)} data-testid="pm-add">+ Add child</BigButton>
      <div className="pm-grid">
        {profiles.map((p) => (
          <Card key={p.id} data-testid={`pm-card-${p.nickname.toLowerCase()}`}>
            <h3><span aria-hidden>{avatarLabel(p.avatar)}</span> {p.nickname}</h3>
            <p>Difficulty: {p.difficulty} · Narration: {p.narrationSpeed}</p>
            <p>Streak counter: {p.accessibilitySettings?.showStreakCounter || "auto"}</p>
            <div className="pm-actions">
              <BigButton variant="ghost" onClick={() => setEditing(p)} data-testid={`pm-edit-${p.nickname.toLowerCase()}`}>Edit</BigButton>
              <BigButton variant="coral" onClick={async () => {
                if (window.prompt(`Type ${p.nickname} to delete this profile`) === p.nickname) {
                  await api.delete(`/profiles/${p.id}`);
                  refresh();
                }
              }} data-testid={`pm-delete-${p.nickname.toLowerCase()}`}>Delete</BigButton>
            </div>
          </Card>
        ))}
      </div>
      {(editing || creating) && (
        <ProfileForm
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false); refresh(); }}
        />
      )}
    </div>
  );
}

function ProfileForm({ initial, onClose }) {
  const [form, setForm] = useState({
    nickname: initial?.nickname || "",
    avatar: initial?.avatar || "fox",
    ageBand: initial?.ageBand || "4-6",
    difficulty: initial?.difficulty || "easy",
    narrationSpeed: initial?.narrationSpeed || "slow",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function save() {
    setBusy(true); setErr(null);
    try {
      if (initial) await api.patch(`/profiles/${initial.id}`, form);
      else await api.post(`/profiles`, form);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="modal">
      <Card className="modal__panel" data-testid="pm-form">
        <h2>{initial ? "Edit profile" : "Add child"}</h2>
        <label>Nickname<input value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} data-testid="pm-form-nickname" /></label>
        <label>Avatar
          <div className="pm-avatars">
            {AVATAR_CHOICES.map((a) => (
              <button key={a} className={`pm-avatar${form.avatar === a ? " pm-avatar--sel" : ""}`}
                onClick={() => setForm({ ...form, avatar: a })} type="button" data-testid={`pm-avatar-${a}`}>
                {avatarLabel(a)}
              </button>
            ))}
          </div>
        </label>
        <label>Age band
          <select value={form.ageBand} onChange={(e) => setForm({ ...form, ageBand: e.target.value })} data-testid="pm-form-age">
            <option value="2-4">2-4</option><option value="3-4">3-4</option><option value="4-6">4-6</option><option value="6-8">6-8</option>
          </select>
        </label>
        <label>Difficulty
          <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} data-testid="pm-form-diff">
            <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
          </select>
        </label>
        <label>Narration speed
          <select value={form.narrationSpeed} onChange={(e) => setForm({ ...form, narrationSpeed: e.target.value })} data-testid="pm-form-speed">
            <option value="slow">Slow</option><option value="normal">Normal</option><option value="fast">Fast</option>
          </select>
        </label>
        {err && <div className="auth-form__error" data-testid="pm-form-error">{JSON.stringify(err)}</div>}
        <div className="modal__actions">
          <BigButton variant="ghost" onClick={onClose}>Cancel</BigButton>
          <BigButton onClick={save} disabled={busy} data-testid="pm-form-save">{busy ? "Saving…" : "Save"}</BigButton>
        </div>
      </Card>
    </div>
  );
}

export function ProgressDetail() {
  const navigate = useNavigate();
  const profileId = window.location.pathname.split("/").pop();
  const [summary, setSummary] = useState(null);
  const [words, setWords] = useState({ items: [], categories: [] });
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get(`/dashboard/${profileId}/summary`).then((r) => setSummary(r.data));
    reloadWords("all");
    // eslint-disable-next-line
  }, [profileId]);
  function reloadWords(f) {
    const params = {};
    if (f === "favorites") params.favorites = true;
    if (f === "practice") params.practice = true;
    if (f === "recent") params.sort = "recent";
    setFilter(f);
    api.get(`/wordbook/${profileId}`, { params }).then((r) => setWords(r.data));
  }
  async function toggleFav(w) {
    await api.post(`/wordbook/${profileId}/favorite`, { learningObjectId: w.learningObjectId, isFavorite: !w.isFavorite });
    reloadWords(filter);
  }

  if (!summary) return <p>Loading…</p>;

  return (
    <div className="progress-detail" data-testid="progress-detail">
      <BigButton variant="ghost" onClick={() => navigate("/parent/dashboard")}>← Overview</BigButton>
      <h1>{summary.profile.nickname}</h1>
      <div className="pm-grid">
        <Card><h3>Levels done</h3><p>{summary.stats.levelsCompleted}</p></Card>
        <Card><h3>Perfect clears</h3><p>{summary.stats.totalPerfectClears}</p></Card>
        <Card><h3>Best streak</h3><p>{summary.stats.streakBest}</p></Card>
        <Card><h3>Words</h3><p>{summary.stats.wordsEncountered}</p></Card>
        <Card><h3>Hints used</h3><p>{summary.stats.hintsUsedTotal}</p></Card>
      </div>
      <h2>Words</h2>
      <div className="wb-filters">
        {["all", "favorites", "practice", "recent"].map((f) => (
          <BigButton key={f} variant={filter === f ? "primary" : "ghost"} onClick={() => reloadWords(f)} data-testid={`wb-filter-${f}`}>
            {f}
          </BigButton>
        ))}
      </div>
      <div className="wb-grid">
        {words.items.map((w) => (
          <Card key={w.learningObjectId} data-testid={`wb-card-${w.canonicalName}`}>
            {w.thumbnailUrl && <img src={w.thumbnailUrl} alt="" className="wb-thumb" />}
            <h4>{w.displayName}</h4>
            <p className="wb-spelling">{(w.spellingSequence || []).join(" ")}</p>
            <p><span className={`wb-dot wb-dot--${w.masteryIndicator}`} /> {w.masteryIndicator}</p>
            <BigButton variant="ghost" onClick={() => toggleFav(w)} data-testid={`wb-fav-${w.canonicalName}`}>
              {w.isFavorite ? "★" : "☆"}
            </BigButton>
          </Card>
        ))}
        {words.items.length === 0 && <p>No words yet.</p>}
      </div>
      <h2>Level progress</h2>
      <table className="pm-table" data-testid="level-progress-table">
        <thead><tr><th>Level</th><th>World</th><th>Status</th><th>Stars</th><th>Best streak</th><th>Perfect clears</th></tr></thead>
        <tbody>
          {summary.levelProgress.map((lp) => (
            <tr key={lp.levelId}><td>{lp.levelName}</td><td>{lp.worldName}</td><td>{lp.status}</td><td>{lp.stars}</td><td>{lp.bestStreak}</td><td>{lp.perfectClears}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
